
import React, { useState } from 'react';
import { RenovationProject, RenovationTask, RenovationExpense, Contractor, Property, RenovationStatus, RenovationType, TaskStatus, ExpenseCategory, RenovationQuote } from '../types';
import { HardHat, Calendar, Users, FileText, Plus, ArrowLeft, BarChart2, Bell, Trash2, Wallet, ScanLine } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// Subcomponents
import GanttChart from './renovation/GanttChart';
import BudgetComparisonTable from './renovation/BudgetComparisonTable';
import InspectionChecklist from './renovation/InspectionChecklist';
import BudgetEditor from './renovation/BudgetEditor';
import SmartScanModal from './renovation/SmartScanModal';

interface RenovationManagerProps {
  properties: Property[];
  renovations: RenovationProject[];
  contractors: Contractor[];
  onUpdateRenovations: (renovations: RenovationProject[]) => void;
  onUpdateContractors: (contractors: Contractor[]) => void;
  lang: 'en' | 'pt';
}

const RenovationManager: React.FC<RenovationManagerProps> = ({ 
  properties, 
  renovations, 
  contractors, 
  onUpdateRenovations, 
  onUpdateContractors,
  lang 
}) => {
  const [selectedProject, setSelectedProject] = useState<RenovationProject | null>(null);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'tasks' | 'budget' | 'financials' | 'team' | 'inspection'>('overview');

  // --- HANDLERS ---

  const handleCreateProject = () => {
    const newProject: RenovationProject = {
      id: Date.now().toString(),
      propertyId: properties[0]?.id || '',
      name: 'New Renovation Project',
      type: RenovationType.Cosmetic,
      status: RenovationStatus.Planned,
      startDate: new Date().toISOString().split('T')[0],
      endDateEstimated: '',
      budget: 10000,
      description: '',
      tasks: [],
      expenses: [],
      inspectionList: [],
      budgets: []
    };
    onUpdateRenovations([...renovations, newProject]);
    setSelectedProject(newProject);
    setView('detail');
  };

  const handleUpdateProject = (updatedProject: RenovationProject) => {
    const updatedList = renovations.map(r => r.id === updatedProject.id ? updatedProject : r);
    onUpdateRenovations(updatedList);
    setSelectedProject(updatedProject); // Keep local state in sync
  };

  const handleDeleteProject = (id: string) => {
      if(window.confirm("Delete this project?")) {
          onUpdateRenovations(renovations.filter(r => r.id !== id));
          setSelectedProject(null);
          setView('list');
      }
  }

  // --- RENDERERS ---

  if (view === 'list') {
    return (
      <div className="p-8 max-w-[1600px] mx-auto pb-24 flex flex-col h-full overflow-y-auto custom-scrollbar">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-4xl font-black text-white tracking-tight mb-1 uppercase flex items-center gap-3">
              <HardHat className="w-8 h-8 text-emerald-500" />
              Construction
            </h2>
            <p className="text-slate-400 font-mono text-xs tracking-widest">PROJECT MANAGEMENT MODULE</p>
          </div>
          <button 
            onClick={handleCreateProject}
            className="bg-emerald-600 text-white px-6 py-2.5 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.3)] border border-emerald-500 flex items-center gap-2 hover:bg-emerald-500 transition-all font-bold"
          >
            <Plus className="w-4 h-4" />
            New Project
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renovations.map(project => {
            const property = properties.find(p => p.id === project.propertyId);
            const totalExpenses = project.expenses.reduce((acc, e) => acc + e.amount, 0);
            const progress = project.tasks.length > 0 
                ? (project.tasks.filter(t => t.status === TaskStatus.Completed).length / project.tasks.length) * 100
                : 0;
            
            const hasAlerts = project.tasks.some(t => t.status !== TaskStatus.Completed && t.endDate && new Date(t.endDate) < new Date());

            return (
              <motion.div 
                key={project.id}
                layoutId={project.id}
                onClick={() => { setSelectedProject(project); setView('detail'); }}
                className="bg-slate-900/60 backdrop-blur-md border border-slate-800 p-6 rounded-3xl hover:border-emerald-500/30 transition-all cursor-pointer group relative overflow-hidden"
              >
                {hasAlerts && (
                    <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl flex items-center gap-1">
                        <Bell className="w-3 h-3" /> Action Needed
                    </div>
                )}

                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-bold text-white text-lg group-hover:text-emerald-400 transition-colors">{project.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{property?.address || 'Unassigned Property'}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
                    project.status === RenovationStatus.InProgress ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                    project.status === RenovationStatus.Planned ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                    'bg-slate-800 text-slate-400 border-slate-700'
                  }`}>
                    {project.status}
                  </span>
                </div>

                <div className="space-y-4">
                    {/* Budget Bar */}
                    <div>
                        <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-slate-400 font-medium">Budget Utilized</span>
                            <span className={`font-bold ${totalExpenses > project.budget ? 'text-red-400' : 'text-slate-200'}`}>
                                {((totalExpenses / (project.budget || 1)) * 100).toFixed(0)}%
                            </span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div 
                                className={`h-full rounded-full ${totalExpenses > project.budget ? 'bg-red-500' : 'bg-blue-500'}`} 
                                style={{ width: `${Math.min((totalExpenses / (project.budget || 1)) * 100, 100)}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between text-[10px] mt-1 text-slate-500 font-mono">
                            <span>€{totalExpenses.toLocaleString()}</span>
                            <span>€{project.budget.toLocaleString()}</span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                        <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-slate-400 font-medium">Completion</span>
                            <span className="text-emerald-400 font-bold">{progress.toFixed(0)}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-emerald-500 rounded-full" 
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5" />
                        {project.startDate}
                    </div>
                    <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" />
                        {new Set(project.tasks.map(t => t.contractorId).filter(Boolean)).size} Pro(s)
                    </div>
                </div>
              </motion.div>
            );
          })}
          
          {renovations.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-20 text-slate-600 border border-dashed border-slate-800 rounded-3xl bg-slate-900/20">
                  <HardHat className="w-16 h-16 mb-4 opacity-20" />
                  <p className="text-lg font-medium">No projects initialized</p>
                  <button onClick={handleCreateProject} className="mt-4 text-emerald-500 hover:text-emerald-400 text-sm font-bold">Start your first renovation</button>
              </div>
          )}
        </div>
      </div>
    );
  }

  // --- DETAIL VIEW ---
  
  if (!selectedProject) return null;

  const property = properties.find(p => p.id === selectedProject.propertyId);
  const totalExpenses = selectedProject.expenses.reduce((acc, e) => acc + e.amount, 0);
  const expenseByCategory = Object.values(ExpenseCategory).map(cat => ({
      name: cat,
      value: selectedProject.expenses.filter(e => e.category === cat).reduce((acc, e) => acc + e.amount, 0)
  })).filter(d => d.value > 0);

  const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="h-full flex flex-col bg-slate-950 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-800 p-4 md:p-6 flex flex-col md:flex-row justify-between md:items-center gap-4 z-20">
            <div className="flex items-center gap-4">
                <button onClick={() => setView('list')} className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h2 className="text-2xl font-black text-white">{selectedProject.name}</h2>
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                        <span className="bg-slate-800 px-2 py-0.5 rounded text-xs font-mono text-slate-300">{selectedProject.type}</span>
                        <span>•</span>
                        <span>{property?.address}</span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3">
                 <select 
                    value={selectedProject.status}
                    onChange={(e) => handleUpdateProject({...selectedProject, status: e.target.value as RenovationStatus})}
                    className="bg-slate-950 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 outline-none"
                 >
                    {Object.values(RenovationStatus).map(s => <option key={s} value={s}>{s}</option>)}
                 </select>
                 <button onClick={() => handleDeleteProject(selectedProject.id)} className="p-2 text-red-400 hover:bg-red-900/30 rounded-lg">
                     <Trash2 className="w-5 h-5" />
                 </button>
            </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 bg-slate-950 px-6 overflow-x-auto">
            {(['overview', 'timeline', 'tasks', 'budget', 'financials', 'team', 'inspection'] as const).map(tab => (
                <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-4 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab 
                        ? 'border-emerald-500 text-emerald-400' 
                        : 'border-transparent text-slate-500 hover:text-slate-300'
                    }`}
                >
                    {tab === 'timeline' ? <div className="flex items-center gap-2"><BarChart2 className="w-4 h-4" /> Timeline</div> : tab}
                </button>
            ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            
            {activeTab === 'overview' && (
                <OverviewTab 
                    selectedProject={selectedProject} 
                    handleUpdateProject={handleUpdateProject} 
                    properties={properties} 
                    totalExpenses={totalExpenses}
                />
            )}

            {activeTab === 'timeline' && (
                <GanttChart 
                    project={selectedProject} 
                    onUpdateTask={(updatedTask) => {
                        const updatedTasks = selectedProject.tasks.map(t => t.id === updatedTask.id ? updatedTask : t);
                        handleUpdateProject({...selectedProject, tasks: updatedTasks});
                    }}
                />
            )}

            {activeTab === 'tasks' && (
                <TasksManager 
                    project={selectedProject} 
                    onUpdate={(updated) => handleUpdateProject(updated)} 
                    contractors={contractors}
                />
            )}

            {activeTab === 'budget' && (
                <BudgetComparisonTable 
                    project={selectedProject} 
                    onUpdate={handleUpdateProject}
                    contractors={contractors}
                    property={property}
                />
            )}

            {activeTab === 'financials' && (
                <FinancialsTab 
                    selectedProject={selectedProject} 
                    handleUpdateProject={handleUpdateProject} 
                    property={property} 
                    totalExpenses={totalExpenses} 
                    expenseByCategory={expenseByCategory} 
                    CHART_COLORS={CHART_COLORS}
                />
            )}

            {activeTab === 'team' && (
                <TeamTab 
                    contractors={contractors} 
                    onUpdateContractors={onUpdateContractors} 
                />
            )}

            {activeTab === 'inspection' && (
                <InspectionChecklist 
                    project={selectedProject} 
                    onUpdate={handleUpdateProject} 
                />
            )}
        </div>
    </div>
  );
};

// --- SUBCOMPONENTS ---

const OverviewTab = ({ selectedProject, handleUpdateProject, properties, totalExpenses }: { 
    selectedProject: RenovationProject, 
    handleUpdateProject: (p: RenovationProject) => void, 
    properties: Property[],
    totalExpenses: number 
}) => {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <div className="lg:col-span-2 space-y-6">
                <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6">
                     <div className="flex justify-between items-center mb-6">
                         <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <FileText className="w-5 h-5 text-emerald-500" /> Project Details
                         </h3>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Project Name</label>
                            <input 
                                type="text" 
                                value={selectedProject.name}
                                onChange={(e) => handleUpdateProject({...selectedProject, name: e.target.value})}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2 text-white outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Property</label>
                            <select 
                                value={selectedProject.propertyId}
                                onChange={(e) => handleUpdateProject({...selectedProject, propertyId: e.target.value})}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2 text-white outline-none focus:border-emerald-500"
                            >
                                {properties.map((p: Property) => <option key={p.id} value={p.id}>{p.address}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Renovation Type</label>
                            <select 
                                value={selectedProject.type}
                                onChange={(e) => handleUpdateProject({...selectedProject, type: e.target.value as RenovationType})}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2 text-white outline-none focus:border-emerald-500"
                            >
                                {Object.values(RenovationType).map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Target Budget (€)</label>
                            <input 
                                type="number" 
                                value={selectedProject.budget}
                                onChange={(e) => handleUpdateProject({...selectedProject, budget: Number(e.target.value)})}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2 text-white outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description</label>
                            <textarea 
                                value={selectedProject.description}
                                onChange={(e) => handleUpdateProject({...selectedProject, description: e.target.value})}
                                rows={4}
                                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2 text-white outline-none focus:border-emerald-500"
                            />
                        </div>
                     </div>
                </div>
            </div>

            <div className="space-y-6">
                 <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6">
                    <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Snapshot</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-slate-500">Spent / Budget</span>
                            <div className="text-right">
                                <p className="text-white font-bold">€{totalExpenses.toLocaleString()}</p>
                                <p className="text-xs text-slate-500">of €{selectedProject.budget.toLocaleString()}</p>
                            </div>
                        </div>
                        <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                            <div 
                                className={`h-full ${totalExpenses > selectedProject.budget ? 'bg-red-500' : 'bg-emerald-500'}`} 
                                style={{ width: `${Math.min((totalExpenses/selectedProject.budget)*100, 100)}%` }}
                            ></div>
                        </div>
                    </div>
                 </div>
            </div>
        </div>
    );
};

const TasksManager = ({ project, onUpdate, contractors }: { project: RenovationProject, onUpdate: (p: RenovationProject) => void, contractors: Contractor[] }) => {
    const [activeQuoteTask, setActiveQuoteTask] = useState<string | null>(null);

    const addTask = () => {
        const newTask: RenovationTask = {
            id: Date.now().toString(),
            title: 'New Task',
            status: TaskStatus.Pending,
            estimatedCost: 0,
            actualCost: 0,
            phase: 'General',
            quotes: []
        };
        onUpdate({ ...project, tasks: [...project.tasks, newTask] });
    };

    const updateTask = (index: number, field: keyof RenovationTask, value: any) => {
        const updatedTasks = [...project.tasks];
        updatedTasks[index] = { ...updatedTasks[index], [field]: value };
        onUpdate({ ...project, tasks: updatedTasks });
    };

    const deleteTask = (index: number) => {
        const updatedTasks = project.tasks.filter((_, i) => i !== index);
        onUpdate({ ...project, tasks: updatedTasks });
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-white">Project Tasks</h3>
                <button onClick={addTask} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors">
                    + Add Task
                </button>
            </div>
            
            <div className="space-y-3">
                <AnimatePresence>
                    {project.tasks.map((task, idx) => (
                        <motion.div 
                            key={task.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl hover:border-slate-700 transition-colors group"
                        >
                            <div className="grid grid-cols-12 gap-4 items-center">
                                <div className="col-span-3 space-y-2">
                                    <input 
                                        value={task.title}
                                        onChange={(e) => updateTask(idx, 'title', e.target.value)}
                                        className="bg-transparent text-white font-medium w-full outline-none border-b border-transparent focus:border-slate-700"
                                        placeholder="Task Name"
                                    />
                                     <input 
                                        value={task.phase || ''}
                                        onChange={(e) => updateTask(idx, 'phase', e.target.value)}
                                        className="bg-transparent text-slate-400 text-xs w-full outline-none"
                                        placeholder="Phase (e.g. Demo)"
                                    />
                                </div>
                                <div className="col-span-2 space-y-2">
                                     <input 
                                        type="date"
                                        value={task.startDate || ''}
                                        onChange={(e) => updateTask(idx, 'startDate', e.target.value)}
                                        className="bg-slate-950 text-slate-300 text-xs rounded border border-slate-800 w-full p-1 outline-none"
                                    />
                                     <input 
                                        type="date"
                                        value={task.endDate || ''}
                                        onChange={(e) => updateTask(idx, 'endDate', e.target.value)}
                                        className="bg-slate-950 text-slate-300 text-xs rounded border border-slate-800 w-full p-1 outline-none"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <select 
                                        value={task.contractorId || ''}
                                        onChange={(e) => updateTask(idx, 'contractorId', e.target.value)}
                                        className="bg-slate-950 text-slate-300 text-xs rounded border border-slate-800 w-full p-1 outline-none"
                                    >
                                        <option value="">Unassigned</option>
                                        {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-span-2">
                                    <select 
                                        value={task.status}
                                        onChange={(e) => updateTask(idx, 'status', e.target.value)}
                                        className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-wide border w-full outline-none ${
                                            task.status === TaskStatus.Completed ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                            task.status === TaskStatus.InProgress ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                            'bg-slate-800 text-slate-500 border-slate-700'
                                        }`}
                                    >
                                        {Object.values(TaskStatus).map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="col-span-2 text-right flex flex-col gap-2">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-slate-500">Est:</span>
                                        <input 
                                            type="number"
                                            value={task.estimatedCost}
                                            onChange={(e) => updateTask(idx, 'estimatedCost', Number(e.target.value))}
                                            className="bg-transparent text-right text-slate-300 w-16 outline-none placeholder-slate-600"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => setActiveQuoteTask(task.id)}
                                        className="text-[10px] bg-slate-800 hover:bg-indigo-600 text-white px-2 py-1 rounded transition-colors flex items-center justify-center gap-1"
                                    >
                                        {task.quotes?.length || 0} Bids
                                    </button>
                                </div>
                                <div className="col-span-1 text-right">
                                    <button onClick={() => deleteTask(idx)} className="text-slate-600 hover:text-red-400 transition-colors">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {activeQuoteTask && (
                    <BudgetEditor 
                        taskTitle={project.tasks.find(t => t.id === activeQuoteTask)?.title || 'Task'}
                        existingQuotes={project.tasks.find(t => t.id === activeQuoteTask)?.quotes || []}
                        onSave={(quotes: RenovationQuote[]) => {
                            const taskIdx = project.tasks.findIndex(t => t.id === activeQuoteTask);
                            updateTask(taskIdx, 'quotes', quotes);
                        }}
                        onClose={() => setActiveQuoteTask(null)}
                    />
                )}
            </div>
        </div>
    );
};

const FinancialsTab = ({ selectedProject, handleUpdateProject, property, totalExpenses, expenseByCategory, CHART_COLORS }: {
    selectedProject: RenovationProject, 
    handleUpdateProject: (p: RenovationProject) => void,
    property?: Property, 
    totalExpenses: number, 
    expenseByCategory: any[], 
    CHART_COLORS: string[]
}) => {
    const [isSmartScanOpen, setIsSmartScanOpen] = useState(false);

    return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
    <div className="space-y-6">
         {/* Expense List */}
         <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6">
             <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-emerald-500" /> Expenses
                </h3>
                <div className="flex gap-2">
                    <button 
                        onClick={() => setIsSmartScanOpen(true)}
                        className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-indigo-500/20"
                    >
                        <ScanLine className="w-3.5 h-3.5" /> Smart Scan
                    </button>
                    <button 
                        onClick={() => {
                            const newExp: RenovationExpense = {
                                id: Date.now().toString(),
                                date: new Date().toISOString().split('T')[0],
                                description: 'New Expense',
                                amount: 0,
                                category: ExpenseCategory.Materials
                            };
                            handleUpdateProject({...selectedProject, expenses: [...selectedProject.expenses, newExp]});
                        }}
                        className="text-xs bg-slate-800 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg transition-colors"
                    >
                        + Add Receipt
                    </button>
                </div>
             </div>
             
             <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                 {selectedProject.expenses.map((exp: RenovationExpense, idx: number) => (
                     <div key={exp.id} className="bg-slate-950 border border-slate-800 p-4 rounded-xl flex justify-between items-center group">
                         <div className="flex-1 grid grid-cols-2 gap-4">
                             <input 
                                value={exp.description}
                                onChange={(e) => {
                                    const updated = [...selectedProject.expenses];
                                    updated[idx].description = e.target.value;
                                    handleUpdateProject({...selectedProject, expenses: updated});
                                }}
                                className="bg-transparent text-white text-sm outline-none border-b border-transparent focus:border-slate-700"
                             />
                              <input 
                                type="number"
                                value={exp.amount}
                                onChange={(e) => {
                                    const updated = [...selectedProject.expenses];
                                    updated[idx].amount = Number(e.target.value);
                                    handleUpdateProject({...selectedProject, expenses: updated});
                                }}
                                className="bg-transparent text-right text-white font-mono text-sm outline-none border-b border-transparent focus:border-slate-700"
                             />
                              <select 
                                value={exp.category}
                                onChange={(e) => {
                                    const updated = [...selectedProject.expenses];
                                    updated[idx].category = e.target.value as ExpenseCategory;
                                    handleUpdateProject({...selectedProject, expenses: updated});
                                }}
                                className="bg-slate-900 text-slate-400 text-xs rounded border border-slate-700 outline-none"
                             >
                                 {Object.values(ExpenseCategory).map(c => <option key={c} value={c}>{c}</option>)}
                             </select>
                              <input 
                                type="date"
                                value={exp.date}
                                onChange={(e) => {
                                    const updated = [...selectedProject.expenses];
                                    updated[idx].date = e.target.value;
                                    handleUpdateProject({...selectedProject, expenses: updated});
                                }}
                                className="bg-transparent text-right text-slate-500 text-xs outline-none"
                             />
                         </div>
                         <button 
                            onClick={() => {
                                const updated = selectedProject.expenses.filter((e: RenovationExpense) => e.id !== exp.id);
                                handleUpdateProject({...selectedProject, expenses: updated});
                            }}
                            className="ml-4 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                         >
                             <Trash2 className="w-4 h-4" />
                         </button>
                     </div>
                 ))}
             </div>
         </div>
    </div>

    <div className="space-y-6">
        {/* Charts */}
        <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6 h-80">
            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">Cost Breakdown</h3>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={expenseByCategory}
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                    >
                        {expenseByCategory.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', color: '#fff' }} />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        </div>

         <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6">
             <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">ROI Projection</h3>
             <div className="flex justify-between items-center mb-4">
                 <span className="text-slate-500">Current Property Value</span>
                 <span className="text-white font-mono">€{property?.currentValue.toLocaleString()}</span>
             </div>
             <div className="flex justify-between items-center mb-4">
                 <span className="text-slate-500">Total Renovation Cost</span>
                 <span className="text-red-400 font-mono">-€{totalExpenses.toLocaleString()}</span>
             </div>
             <div className="pt-4 border-t border-slate-800 flex justify-between items-center">
                 <span className="text-emerald-400 font-bold">Est. Post-Reno Value</span>
                 <span className="text-emerald-400 font-bold font-mono text-xl">
                     €{((property?.currentValue || 0) + (totalExpenses * 1.5)).toLocaleString()}
                     <span className="text-xs font-normal text-slate-500 ml-2">(Est. 1.5x ROI)</span>
                 </span>
             </div>
         </div>
    </div>
    
    {isSmartScanOpen && (
        <SmartScanModal 
            onSave={(expense) => handleUpdateProject({...selectedProject, expenses: [...selectedProject.expenses, expense]})}
            onClose={() => setIsSmartScanOpen(false)}
        />
    )}
 </div>
)};

const TeamTab = ({ contractors, onUpdateContractors }: { contractors: Contractor[], onUpdateContractors: (c: Contractor[]) => void }) => (
    <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">Contractors Directory</h3>
            <button 
                onClick={() => {
                    const newContractor: Contractor = {
                        id: Date.now().toString(),
                        name: 'New Pro',
                        specialty: 'General',
                        rating: 0
                    };
                    onUpdateContractors([...contractors, newContractor]);
                }}
                className="bg-slate-800 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-bold"
            >
                + Add Professional
            </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contractors.map((c, idx) => (
                <div key={c.id} className="bg-slate-900/50 border border-slate-800 p-6 rounded-2xl hover:border-emerald-500/30 transition-all group relative">
                    <button 
                        onClick={() => onUpdateContractors(contractors.filter(con => con.id !== c.id))}
                        className="absolute top-4 right-4 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>

                    <div className="space-y-4">
                        <div>
                            <label className="text-[10px] text-slate-500 font-bold uppercase">Name</label>
                            <input 
                                value={c.name}
                                onChange={(e) => {
                                    const updated = [...contractors];
                                    updated[idx].name = e.target.value;
                                    onUpdateContractors(updated);
                                }}
                                className="w-full bg-transparent text-white font-bold text-lg outline-none border-b border-transparent focus:border-emerald-500"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] text-slate-500 font-bold uppercase">Specialty</label>
                                <input 
                                    value={c.specialty}
                                    onChange={(e) => {
                                        const updated = [...contractors];
                                        updated[idx].specialty = e.target.value;
                                        onUpdateContractors(updated);
                                    }}
                                    className="w-full bg-transparent text-slate-300 text-sm outline-none border-b border-transparent focus:border-emerald-500"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 font-bold uppercase">Rate / Hr</label>
                                <input 
                                    type="number"
                                    value={c.hourlyRate || ''}
                                    placeholder="-"
                                    onChange={(e) => {
                                        const updated = [...contractors];
                                        updated[idx].hourlyRate = Number(e.target.value);
                                        onUpdateContractors(updated);
                                    }}
                                    className="w-full bg-transparent text-slate-300 text-sm outline-none border-b border-transparent focus:border-emerald-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] text-slate-500 font-bold uppercase">Contact</label>
                            <input 
                                value={c.phone || ''}
                                placeholder="Phone / Email"
                                onChange={(e) => {
                                    const updated = [...contractors];
                                    updated[idx].phone = e.target.value;
                                    onUpdateContractors(updated);
                                }}
                                className="w-full bg-transparent text-slate-400 text-sm outline-none border-b border-transparent focus:border-emerald-500"
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export default RenovationManager;
